import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import { tagClasses } from "../lib/tags";
import { formatNumber } from "../lib/format";
import { useSettings } from "../SettingsContext";
import MoneyInput from "./MoneyInput";
import { MONTHS_SHORT } from "../lib/months";

// Flat "all transactions for the year" table view (auto-saving)
const AUTOSAVE_DEBOUNCE_MS = 700;

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  return `${parts[1]}/${parts[2]}`; // MM/DD
};

const TransactionsTable = ({ statements, accountType, onChanged }) => {
  const { settings, openLabelMapping, addSupplier, addEmployee } = useSettings();
  const [rows, setRows] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | pending | saving | saved | error
  const debounceRef = useRef(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const savedFlashRef = useRef(null);

  const splitCols =
    accountType === "Credit Card"
      ? { left: "Payment", right: "Purchase" }
      : accountType === "Bank Account"
      ? { left: "Withdrawal", right: "Deposit" }
      : null;
  // Column where a "refund" can be marked: CC payments (left), Bank deposits (right)
  const refundCol =
    accountType === "Credit Card" ? "left" : accountType === "Bank Account" ? "right" : null;

  useEffect(() => {
    // Skip rebuilding local rows while an edit is queued or a save is
    // in flight — otherwise the incoming `statements` prop (from the
    // parent's post-save refetch) can momentarily overwrite an
    // optimistic UI change (e.g. HST checkbox appearing to un-check
    // for ~1s and then re-check).
    if (saveStatus === "pending" || saveStatus === "saving") return;
    const flat = [];
    for (const s of statements) {
      for (const t of s.transactions || []) {
        flat.push({
          ...t,
          _statementId: s.id,
          _month: s.month,
        });
      }
    }
    flat.sort((a, b) => {
      if (a._month !== b._month) return a._month - b._month;
      return (a.date || "").localeCompare(b.date || "");
    });
    setRows(flat);
  }, [statements, saveStatus]);

  const buildPayloadByStatement = (source) => {
    const groups = new Map();
    for (const r of source) {
      if (!groups.has(r._statementId)) groups.set(r._statementId, []);
      const { _statementId, _month, _col, ...tx } = r;
      groups.get(_statementId).push({
        ...tx,
        amount: tx.amount === "" || tx.amount == null ? null : parseFloat(tx.amount),
        tax: tx.tax === "" || tx.tax == null ? null : parseFloat(tx.tax),
        note: tx.note || null,
        tag: tx.tag || null,
        tag_auto: !!tx.tag_auto,
        hst: !!tx.hst,
        refund: !!tx.refund,
      });
    }
    // Include any originating statements that lost all their rows (deleted last tx)
    for (const s of statements) {
      if (!groups.has(s.id)) groups.set(s.id, []);
    }
    return groups;
  };

  const doSave = async (source) => {
    inFlightRef.current = true;
    setSaveStatus("saving");
    try {
      const groups = buildPayloadByStatement(source);
      await Promise.all(
        Array.from(groups.entries()).map(([sid, txs]) =>
          axios.put(`${API}/statements/${sid}`, { transactions: txs })
        )
      );
      setSaveStatus("saved");
      if (savedFlashRef.current) clearTimeout(savedFlashRef.current);
      savedFlashRef.current = setTimeout(() => setSaveStatus("idle"), 1500);
      if (onChanged) await onChanged();
    } catch (e) {
      console.error("Auto-save failed:", e);
      setSaveStatus("error");
      toast.error("Auto-save failed");
    } finally {
      inFlightRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        // Something was queued while saving — trigger another debounce cycle
        scheduleSaveFromLatest();
      }
    }
  };

  // Used only when a save just finished and another was queued mid-flight
  const scheduleSaveFromLatest = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus("pending");
    debounceRef.current = setTimeout(() => {
      if (inFlightRef.current) {
        pendingRef.current = true;
        return;
      }
      doSave(rows);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  // Flush on unmount so we don't lose late edits
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedFlashRef.current) clearTimeout(savedFlashRef.current);
    };
  }, []);

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = prev.map((r, i) => (i === idx ? { ...r, ...patch } : r));
      // Schedule save from within setter to see latest rows in closure
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSaveStatus("pending");
      debounceRef.current = setTimeout(() => {
        if (inFlightRef.current) {
          pendingRef.current = true;
          return;
        }
        doSave(next);
      }, AUTOSAVE_DEBOUNCE_MS);
      return next;
    });
  };

  const removeRow = (idx) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSaveStatus("pending");
      debounceRef.current = setTimeout(() => {
        if (inFlightRef.current) {
          pendingRef.current = true;
          return;
        }
        doSave(next);
      }, AUTOSAVE_DEBOUNCE_MS);
      return next;
    });
  };

  const amountCell = (r) => {
    const a = r.amount;
    if (a === "" || a == null) return { left: "", right: "" };
    const n = typeof a === "number" ? a : parseFloat(a);
    const abs = String(a).replace(/^-/, "");
    const isLeft = r._col ? r._col === "left" : !isNaN(n) && n < 0;
    return isLeft ? { left: abs, right: "" } : { left: "", right: abs };
  };

  const tagKindMap = useMemo(() => {
    const m = {};
    for (const t of settings.tags || []) m[t.label] = t.kind || "supplier";
    return m;
  }, [settings.tags]);

  if (rows.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg bg-white p-6 text-sm text-gray-500 text-center" data-testid="transactions-table-empty">
        No transactions for this year yet.
      </div>
    );
  }

  const totals = rows.reduce(
    (acc, r) => {
      const n = typeof r.amount === "number" ? r.amount : parseFloat(r.amount);
      if (!isNaN(n) && !r.refund) {
        if (n < 0) acc.left += Math.abs(n);
        else acc.right += n;
        acc.net += n;
      }
      const t = typeof r.tax === "number" ? r.tax : parseFloat(r.tax);
      if (!isNaN(t)) acc.tax += r.refund ? -t : t;
      return acc;
    },
    { left: 0, right: 0, net: 0, tax: 0 }
  );
  const fmtTotal = (v) => formatNumber(v, settings);
  const showDollar = !!settings?.currency_symbol;
  const totalCell = (v) =>
    showDollar ? (
      <span className="flex items-center justify-between">
        <span className="text-gray-400 font-normal">$</span>
        <span>{fmtTotal(v)}</span>
      </span>
    ) : (
      fmtTotal(v)
    );

  const entitiesForTag = (tagLabel) => {
    const kind = tagKindMap[tagLabel] || "supplier";
    const list = kind === "employee" ? settings.employees : settings.suppliers;
    return { kind, list: (list || []).filter((e) => e.tag === tagLabel) };
  };

  const datalistIdFor = (tagLabel) => {
    const i = (settings.tags || []).findIndex((t) => t.label === tagLabel);
    return i >= 0 ? `entities-dl-${i}` : undefined;
  };

  const canSaveEntity = (r) => {
    const note = (r.note || "").trim();
    if (!r.tag || !note) return false;
    const { list } = entitiesForTag(r.tag);
    return !list.some((e) => e.name.toLowerCase() === note.toLowerCase());
  };

  const saveEntity = (r) => {
    const { kind } = entitiesForTag(r.tag);
    if (kind === "employee") return addEmployee(r.note, r.tag);
    return addSupplier(r.note, r.tag);
  };

  const refundToggle = (r, idx, col) => {
    if (refundCol !== col) return null;
    if (amountCell(r)[col] === "" && !r.refund) return null;
    return (
      <button
        onClick={() => updateRow(idx, { refund: !r.refund })}
        data-testid={`year-tx-refund-toggle-${idx}`}
        title={r.refund ? "Unmark refund" : "Mark as refund (excluded from income/Sales totals)"}
        className={`flex-shrink-0 transition-opacity ${
          r.refund ? "text-red-500 hover:text-red-700" : "opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500"
        }`}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
        </svg>
      </button>
    );
  };

  const statusLabel = {
    pending: "Editing…",
    saving: "Saving…",
    saved: "Saved",
    error: "Save failed",
    idle: "",
  }[saveStatus];

  return (
    <div data-testid="year-transactions-table">
      {(settings.tags || []).map((t, i) => {
        const kind = t.kind || "supplier";
        const list = ((kind === "employee" ? settings.employees : settings.suppliers) || []).filter(
          (e) => e.tag === t.label
        );
        return (
          <datalist key={t.id || i} id={`entities-dl-${i}`}>
            {list.map((e) => (
              <option key={e.id || e.name} value={e.name} />
            ))}
          </datalist>
        );
      })}
      <div className="flex items-center justify-end mb-2 h-4">
        {statusLabel && (
          <span
            data-testid="year-transactions-status"
            className={`text-xs ${saveStatus === "error" ? "text-red-600" : saveStatus === "saved" ? "text-green-600" : "text-gray-500"}`}
          >
            {saveStatus === "saving" && (
              <svg className="inline w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {statusLabel}
          </span>
        )}
      </div>
      <div className="border border-gray-200 rounded-lg bg-white overflow-auto max-h-[65vh]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
              <th className="px-3 py-2 w-20 font-medium">Date</th>
              <th className="px-2 py-2 w-14 font-medium">Month</th>
              <th className="px-3 py-2 font-medium">Description</th>
              {splitCols ? (
                <>
                  <th className="px-3 py-2 w-28 font-medium text-right">{splitCols.left}</th>
                  <th className="px-3 py-2 w-28 font-medium text-right">{splitCols.right}</th>
                </>
              ) : (
                <th className="px-3 py-2 w-28 font-medium text-right">Amount</th>
              )}
              <th className="px-3 py-2 w-36 font-medium">Tag</th>
              <th className="px-3 py-2 min-w-[10rem] font-medium">Notes</th>
              <th className="px-2 py-2 w-12 font-medium text-center">HST</th>
              <th className="px-3 py-2 w-24 font-medium text-right">Tax</th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, idx) => (
              <tr key={`${r._statementId}-${r.id || idx}`} className="group hover:bg-blue-50/60 focus-within:bg-blue-50/60 transition-colors" data-testid={`year-tx-row-${idx}`}>
                <td className="px-3 py-1.5 text-sm text-gray-700 whitespace-nowrap" data-testid={`year-tx-date-${idx}`}>
                  {fmtDateShort(r.date)}
                </td>
                <td className="px-2 py-1.5 text-xs text-gray-500 uppercase">
                  {MONTHS_SHORT[r._month - 1]}
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={r.description || ""}
                      placeholder="Description"
                      onChange={(e) => updateRow(idx, { description: e.target.value })}
                      className="w-full border-0 bg-transparent text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5"
                    />
                    <button
                      onClick={() => openLabelMapping(r.description || "")}
                      data-testid={`year-tx-copy-mapping-${idx}`}
                      title="Copy to label mappings"
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 flex-shrink-0 transition-opacity"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </button>
                  </div>
                </td>
                {splitCols ? (
                  <>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-0.5">
                        {refundToggle(r, idx, "left")}
                        {showDollar && <span className="text-xs text-gray-400 flex-shrink-0">$</span>}
                        <MoneyInput
                          value={amountCell(r).left}
                          allowNegative={false}
                          data-testid={`year-tx-amount-left-${idx}`}
                          onChange={(v) => {
                            updateRow(idx, { amount: v === "" ? "" : "-" + v.replace(/^-/, ""), _col: "left" });
                          }}
                          className={`w-full border-0 bg-transparent text-sm ${r.refund && refundCol === "left" && amountCell(r).left !== "" ? "text-red-600 font-medium" : "text-gray-900"} text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5`}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-0.5">
                        {refundToggle(r, idx, "right")}
                        {showDollar && <span className="text-xs text-gray-400 flex-shrink-0">$</span>}
                        <MoneyInput
                          value={amountCell(r).right}
                          allowNegative={false}
                          data-testid={`year-tx-amount-right-${idx}`}
                          onChange={(v) => {
                            updateRow(idx, { amount: v.replace(/^-/, ""), _col: "right" });
                          }}
                          className={`w-full border-0 bg-transparent text-sm ${r.refund && refundCol === "right" && amountCell(r).right !== "" ? "text-red-600 font-medium" : "text-gray-900"} text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5`}
                        />
                      </div>
                    </td>
                  </>
                ) : (
                  <td className="px-3 py-1.5">
                    <div className="flex items-center">
                      {showDollar && <span className="text-xs text-gray-400 flex-shrink-0">$</span>}
                      <MoneyInput
                        value={r.amount ?? ""}
                        onChange={(v) => updateRow(idx, { amount: v })}
                        className="w-full border-0 bg-transparent text-sm text-gray-900 text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5"
                      />
                    </div>
                  </td>
                )}
                <td className="px-3 py-1.5">
                  {r.tag ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${tagClasses(r.tag, settings.tags)} ${r.tag_auto ? "border-dashed opacity-60" : ""}`}
                        data-testid={`year-tx-tag-${idx}`}
                        title={r.tag_auto ? "Auto-labelled from your mappings — click ✓ to confirm" : undefined}
                      >
                        {r.tag}
                        {r.tag_auto && (
                          <button
                            onClick={() => updateRow(idx, { tag_auto: false })}
                            data-testid={`year-tx-tag-confirm-${idx}`}
                            className="hover:text-green-700 font-bold leading-none"
                            title="Confirm tag"
                          >
                            ✓
                          </button>
                        )}
                        <button
                          onClick={() => updateRow(idx, { tag: null, tag_auto: false })}
                          data-testid={`year-tx-tag-remove-${idx}`}
                          className="hover:text-red-600 font-bold leading-none"
                          title="Remove tag"
                        >
                          ×
                        </button>
                      </span>
                    ) : (
                      <select
                        value=""
                        onChange={(e) => e.target.value && updateRow(idx, { tag: e.target.value, tag_auto: false })}
                        data-testid={`year-tx-tag-select-${idx}`}
                        className="w-8 text-xs text-gray-400 border border-dashed border-gray-300 rounded bg-transparent cursor-pointer focus:outline-none hover:border-gray-400 hover:text-gray-600 py-0.5 text-center"
                        title="Add a tag"
                      >
                        <option value="">＋</option>
                        {(settings.tags || []).map((t) => (
                          <option key={t.id || t.label} value={t.label}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1">
                    {(() => {
                      const rowKind = r.tag ? (tagKindMap[r.tag] || "supplier") : null;
                      const saveTitle = `Save "${(r.note || "").trim()}" as ${
                        rowKind === "employee" ? "an employee" : "a supplier"
                      } under ${r.tag}`;
                      const placeholder = r.tag
                        ? rowKind === "employee"
                          ? "Employee or note"
                          : "Supplier or note"
                        : "—";
                      return (
                        <>
                          <input
                            type="text"
                            value={r.note || ""}
                            placeholder={placeholder}
                            list={r.tag ? datalistIdFor(r.tag) : undefined}
                            data-testid={`year-tx-note-${idx}`}
                            onChange={(e) => updateRow(idx, { note: e.target.value })}
                            className="flex-1 min-w-0 border-0 bg-transparent text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5"
                          />
                          {canSaveEntity(r) && (
                            <button
                              onClick={() => saveEntity(r)}
                              data-testid={`year-tx-save-supplier-${idx}`}
                              title={saveTitle}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-teal-600 flex-shrink-0 transition-opacity"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m-2-2h4" />
                              </svg>
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    type="checkbox"
                    checked={!!r.hst}
                    data-testid={`year-tx-hst-${idx}`}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const patch = { hst: checked };
                      if (checked) {
                        const n = typeof r.amount === "number" ? r.amount : parseFloat(r.amount);
                        if (!isNaN(n)) patch.tax = ((Math.abs(n) * 0.13) / 1.13).toFixed(2);
                      }
                      updateRow(idx, patch);
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center">
                    {showDollar && <span className="text-xs text-gray-400 flex-shrink-0">$</span>}
                    <MoneyInput
                      value={
                        r.refund && r.tax !== "" && r.tax != null
                          ? "-" + String(r.tax).replace(/^-/, "")
                          : r.tax ?? ""
                      }
                      allowNegative={false}
                      data-testid={`year-tx-tax-${idx}`}
                      onChange={(v) => updateRow(idx, { tax: v.replace(/^-/, "") })}
                      className={`w-full border-0 bg-transparent text-sm ${r.refund && r.tax !== "" && r.tax != null ? "text-red-600 font-medium" : "text-gray-900"} text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5`}
                    />
                  </div>
                </td>
                <td className="px-2 py-1.5 text-center">
                  <button
                    onClick={() => removeRow(idx)}
                    data-testid={`year-tx-delete-${idx}`}
                    className="text-gray-300 hover:text-red-500 text-sm font-bold"
                    title="Remove transaction"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-10 bg-gray-50 border-t border-gray-200" data-testid="year-transactions-totals">
            <tr className="text-sm font-semibold text-gray-900">
              <td className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide" colSpan={3}>
                Totals · {rows.length} transaction{rows.length === 1 ? "" : "s"}
              </td>
              {splitCols ? (
                <>
                  <td className="px-3 py-2 text-right whitespace-nowrap" data-testid="year-tx-total-left">
                    {totalCell(totals.left)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap" data-testid="year-tx-total-right">
                    {totalCell(totals.right)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap text-right" data-testid="year-tx-total-net">
                    <span className="mr-1">Net:</span>
                    {showDollar && <span className="text-gray-400 mr-1">$</span>}
                    <span>{fmtTotal(totals.net)}</span>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-3 py-2 text-right whitespace-nowrap" data-testid="year-tx-total-net">
                    {totalCell(totals.net)}
                  </td>
                  <td></td>
                </>
              )}
              <td></td>
              <td></td>
              <td className="px-3 py-2 text-right whitespace-nowrap" data-testid="year-tx-total-tax">
                {totalCell(totals.tax)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TransactionsTable;
